import { AppRoutes } from '@/constants/routes';
import { Permissions } from '@/constants/permissions';
import type { Guide } from './types';

/**
 * Settling documents from inside a voucher, and the ageing that shows what is left.
 *
 * One section because they are two halves of the same job: allocation decides what a payment
 * clears, ageing is the report that shows what it did not. A reader arriving at "why is this
 * invoice still in 90+ days" needs both.
 */

export const allocationAgeingGuides: Guide[] = [
  // ── 1. Allocating inside the voucher ───────────────────────────────────────
  {
    slug: 'allocation-in-voucher',
    title: 'Settling documents without leaving the voucher',
    section: 'allocations-ageing',
    level: 'Basics',
    summary:
      'Pick the bills a payment clears, or the invoices a receipt clears, from inside the '
      + 'transaction — in full, in part, or oldest first.',
    menuPath: 'Accounts Payable / Accounts Receivable',
    screens: [
      { label: 'AP Payments', route: AppRoutes.AP_PAYMENTS, permission: Permissions.AP_PAYMENT_VIEW },
      { label: 'AR Receipts', route: AppRoutes.AR_RECEIPTS, permission: Permissions.AR_RECEIPT_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'Both screens carry the same panel, on a tab beside the payment lines — “Bills Settled” on '
        + 'a payment, “Invoices Settled” on a receipt. It lists the party’s outstanding documents '
        + 'in the voucher’s currency, so there is no second screen to visit and nothing to '
        + 'reconcile afterwards.' },

      { kind: 'heading', text: 'Allocating' },
      { kind: 'steps', steps: [
        { title: 'Tick a document to settle it',
          detail:
            'The amount fills in with a single click — the document’s outstanding balance, or '
            + 'whatever the voucher can still cover, whichever is smaller. There is nothing to type '
            + 'for the ordinary case of clearing a bill in full.' },
        { title: 'Or press “Oldest first”',
          detail:
            'Applies the whole voucher across the outstanding documents from the oldest due date '
            + 'down, until the money runs out. This is what an AP clerk would do by hand.' },
        { title: 'Adjust for a partial settlement',
          detail:
            'The filled amount is editable. It is capped at the document’s own outstanding balance, '
            + 'because a document cannot be settled for more than it is owed.' },
        { title: 'Leave the rest unapplied',
          detail:
            'Anything you do not allocate stays as an on-account credit on the party’s ledger, '
            + 'ready to be applied to a future document.' },
      ] },

      { kind: 'callout', tone: 'tip', title: 'The three numbers under the grid',
        text:
          'Amount to pay, Settling, and On account. When the third is zero the voucher is fully '
          + 'applied; when it is positive the difference is sitting on account; when it turns red '
          + 'the voucher is trying to settle more than it pays, and the save will be refused.' },

      { kind: 'heading', text: 'What a single click actually allocates' },
      { kind: 'para', text:
        'The outstanding balance, or the voucher’s remaining amount if that is smaller. So a '
        + '30,000 payment against a 100,000 bill fills in 30,000, not 100,000. That is the useful '
        + 'answer: filling the full 100,000 would immediately over-apply the voucher and have to be '
        + 'corrected before it could save.' },

      { kind: 'callout', tone: 'note', title: 'When the balances actually move',
        text:
          'At posting, not at save. A draft voucher settles nothing — reducing a supplier’s balance '
          + 'for a payment that has not posted would misstate the ledger and put the control '
          + 'account out of step with its sub-ledger. The picks are re-checked against the current '
          + 'balances at posting time, so a bill someone else settled in the meantime is caught.' },

      { kind: 'heading', text: 'Why a document you expected is missing' },
      { kind: 'list', items: [
        'It is in a different currency — the list is filtered to the voucher’s currency.',
        'It is already fully settled, by this voucher or another.',
        'No party is selected yet — pick the supplier or customer first.',
      ] },
    ],
    related: ['allocation-ageing-slabs', 'ap-payment-bills'],
  },

  // ── 2. Ageing slabs ────────────────────────────────────────────────────────
  {
    slug: 'allocation-ageing-slabs',
    title: 'Ageing bands, and setting your own',
    section: 'allocations-ageing',
    level: 'Advanced',
    summary:
      'The standard ladder, how to define alternative bands for debtors or creditors, and how to '
      + 'run a report on a chosen set.',
    menuPath: 'Setup → Ageing Profiles',
    screens: [
      { label: 'Ageing Profiles',  route: AppRoutes.AGEING_PROFILE_SETUP, permission: Permissions.AGEING_PROFILE_VIEW },
      { label: 'Ageing Analysis',  route: AppRoutes.FIN_AGEING,           permission: Permissions.FIN_OPEN_ITEM_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'Out of the box both ageing reports use a standard ladder, and nothing needs configuring '
        + 'for them to work. A profile replaces that ladder with bands of your own.' },

      { kind: 'heading', text: 'Profiles are per party type' },
      { kind: 'para', text:
        'Debtors and creditors age on different terms — you chase a customer at 30 days and your '
        + 'own supplier terms may run to 60 — so a profile belongs to CUSTOMER or SUPPLIER, and the '
        + 'two lists never mix. Marking one as the default makes it what the report uses when '
        + 'nobody chooses otherwise.' },

      { kind: 'heading', text: 'Defining bands' },
      { kind: 'steps', steps: [
        { title: 'Name the profile and pick the party type',
          where: 'Setup → Ageing Profiles' },
        { title: 'Add the bands, in order',
          detail:
            'Each band has a label, a first day and a last day. The first band starts at day 0 and '
            + 'the last one is open-ended.' },
        { title: 'Make them meet exactly',
          detail:
            'Band 2 must start the day after band 1 ends. 0–30 then 31–60, not 0–30 then 40–60, and '
            + 'not 0–30 then 20–60.' },
        { title: 'Save, and optionally set it as the default' },
      ] },

      { kind: 'callout', tone: 'warn', title: 'Gaps and overlaps are refused',
        text:
          'A gap drops outstanding money — an invoice 35 days old would appear in no column at all. '
          + 'An overlap counts it twice. Either way the columns stop adding up to the total, which '
          + 'is the one thing an ageing report has to do. The save tells you which band is wrong '
          + 'and what it should start at.' },

      { kind: 'heading', text: 'Running a report on chosen bands' },
      { kind: 'para', text:
        'The Ageing Bands control in the report parameters lists the profiles for the party type on '
        + 'screen, with “Company default” as the first option. Switching profiles re-runs the '
        + 'report; the bands and their headings change with it.' },

      { kind: 'callout', tone: 'note', title: 'The totals never change',
        text:
          'Bands decide how the outstanding amount is split across columns, not how much is '
          + 'outstanding. Two runs of the same report on different profiles show the same grand '
          + 'total — if they do not, the bands have a gap or an overlap.' },

      { kind: 'para', text:
        'The profile in use is named on screen and written into the exported file, along with '
        + 'whether the ageing ran by due date or document date and the as-at date. Band columns are '
        + 'meaningless without the ladder that produced them: two exports with identical headings '
        + 'can come from different profiles, and a file that does not say which is not evidence of '
        + 'anything.' },

      { kind: 'heading', text: 'Due date or document date' },
      { kind: 'para', text:
        'Age by due date to see what is overdue against the terms you agreed. Age by document date '
        + 'to see how long the document has existed regardless of terms. The same bands apply '
        + 'either way; only the day counted from changes.' },
    ],
    related: ['allocation-in-voucher'],
  },
];
